import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { getRouteApi } from '@tanstack/react-router';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { spPromoRequisitionInfiniteQueryOptions } from '../api/productRequisition.api';
import { RequisitionFilterPanel } from '../components/RequisitionFilterPanel';
import { getSpPromoRequisitionColumns } from '../components/RequisitionColumns';
import { SpPromoMembersModal } from '../components/SpPromoMembersModal';
import { useRequisitionFilters } from '../hooks/useRequisitionFilters';
import type {
  SpPromoRequisitionRow,
  SpPromoRequisitionSearch,
} from '../types/productRequisition.types';

const route = getRouteApi('/_auth/product/sp-promotional-requisition');

const NUMERIC_KEYS = [
  'userId',
  'specimenId',
  'districtId',
  'divisionId',
  'bookTypeId',
  'instituteId',
  'bookEditionId',
] as const;

export function SpPromoRequisitionPage() {
  const routeSearch = route.useSearch();
  const routeNavigate = route.useNavigate();
  const controller = useRequisitionFilters({
    search: routeSearch,
    numericKeys: NUMERIC_KEYS,
    onApplyFilters: (next) =>
      void routeNavigate({ search: (previous) => ({ ...previous, ...next }), replace: true }),
    onApplySearch: (text) =>
      void routeNavigate({ search: (previous) => ({ ...previous, search: text }), replace: true }),
    withDateRange: true,
  });
  const params = controller.params as SpPromoRequisitionSearch;

  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(spPromoRequisitionInfiniteQueryOptions(params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [drilled, setDrilled] = useState<SpPromoRequisitionRow | null>(null);

  const columns = useMemo(() => getSpPromoRequisitionColumns(setDrilled), []);
  const totals = firstPageMeta?.totals;

  const stats = [
    { key: 'required', label: 'Requisition Qty', value: totals?.required },
    { key: 'teacher', label: 'Teacher Qty', value: totals?.teacher },
    { key: 'targetSale', label: 'Target Sale Qty', value: totals?.targetSale },
    { key: 'approved', label: 'Reviewed Qty', value: totals?.approved },
  ];

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="SP Promotional Requisition"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={params.dateFrom}
            dateTo={params.dateTo}
          />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <RequisitionFilterPanel filters={controller} withDateRange withMember withGeo withBook />
      </FilterPanelCollapse>

      <StatCardGrid stats={stats} isLoading={isLoading} isError={loadError !== null} />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the promotional requisitions"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="specimenName"
          emptyMessage="No requisition was raised in the period"
          {...loadMoreProps}
        />
      )}

      <SpPromoMembersModal specimen={drilled} params={params} onClose={() => setDrilled(null)} />
    </div>
  );
}
